import { User } from '../../../../types'
import { Message } from '../../../../types/state'
import { loadUserIfNeeded } from '../../../actions/loadUserIfNeeded'
import { URL_MFC_SHARE } from '../../../constants/urls'
import { slugToID } from '../users'

const insertLoungeModelPreviews = async (
    text: string,
): Promise<[string, { slug: string; id: number }[]]> => {
    const words = text.split(' ')

    const modelPreviews = []
    const wordsPromises = words.map(async (word) => {
        if (!word) return word
        const possibleSlug = word.toLowerCase()
        const id = slugToID[possibleSlug]
        if (!id || word.length < 5) {
            return word
        }

        const user = await loadUserIfNeeded(id)

        if (!user) {
            return word
        }

        const { isLounge, isModel, slug } = user

        if (isModel && !isLounge) {
            if (!modelPreviews.includes(id)) {
                modelPreviews.push({ id, slug })
            }
            return `{{model|${id}}}`
        }

        return word
    })

    const resolvedWords = await Promise.all(wordsPromises)

    return [resolvedWords.join(' '), modelPreviews]
}

export const processLoungeMessage = async (
    message: Message,
    isLounge: boolean,
): Promise<[Message, { slug: string; id: number }[]]> => {
    if (!isLounge || message.tip) {
        return [message, []]
    }

    const { text } = message
    const [processedText, modelPreviews] = await insertLoungeModelPreviews(text)
    return [{ ...message, text: processedText }, modelPreviews]
}

const generateExtraContentText = (model: User) => {
    const {
        shareRecordings,
        name: modelName,
        hasWhiteboard,
        shareAlbums,
    } = model

    const hasShareContent = shareAlbums > 0 || shareRecordings > 0
    const hasRecordings = shareRecordings > 0
    const hasAlbums = shareAlbums > 0
    const whiteboardMsg = hasWhiteboard
        ? '{{whiteboard}}' + (hasShareContent ? ' ' : '')
        : ''
    const albumsMsg = `{{MFCShareAlbums|${URL_MFC_SHARE}/${modelName}/albums|${shareAlbums}}}`
    const recordingsMsg = `{{MFCShareRecordings|${URL_MFC_SHARE}/${modelName}/recordings|${shareRecordings}}}`

    if (!hasShareContent) {
        return whiteboardMsg
    }

    if (hasAlbums && !hasRecordings) {
        return `${whiteboardMsg}${albumsMsg}`
    }

    if (!hasAlbums && hasRecordings) {
        return `${whiteboardMsg}${recordingsMsg}`
    }

    if (hasAlbums && hasRecordings) {
        return `${whiteboardMsg}${albumsMsg} ${recordingsMsg}`
    }
}

export const generateExtraContentMessage = (model: User) => {
    const message: Message = {
        text: generateExtraContentText(model),
        system: true,
        extraContent: true,
    }

    return message
}
